/**
 * Expense Overview
 * Consolidated dashboard showing total business expenses.
 * totalSpend from the expense summary API already includes both
 * common expenses and active subscriptions (joined at the database level).
 */

import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  DollarSign,
  Receipt,
  Repeat,
  Hash,
  CheckCircle,
  Loader2,
  AlertTriangle,
  Landmark,
  CalendarClock,
  TrendingUp,
  TrendingDown,
  Minus,
  CreditCard,
  Repeat as RepeatIcon,
} from 'lucide-react';

interface ExpenseSummaryData {
  summary: {
    totalSpend: number;
    categoryCount: number;
    expenseCount: number;
    taxDeductibleTotal: number;
  };
  categories: Array<{
    categoryName: string;
    categorySlug: string;
    categoryIcon: string | null;
    totalAmount: number;
    expenseCount: number;
  }>;
}

interface ExpectedMonthlyData {
  categories: Array<{
    categoryName: string;
    categorySlug: string;
    categoryIcon: string | null;
    monthlyAmount: number;
    itemCount: number;
  }>;
  totalMonthly: number;
}

// Only the expiring-subscription alert is consumed here; the recurring-by-cycle
// query supplies the cost rollups now.
interface SubscriptionOverviewData {
  expiringCount: number;
}

interface RecurringByCycleData {
  totalWeeklyCost: number;
  totalMonthlyCost: number;
  totalQuarterlyCost: number;
  totalSemiAnnualCost: number;
  totalAnnualCost: number;
  weeklyCount: number;
  monthlyCount: number;
  quarterlyCount: number;
  semiAnnualCount: number;
  annualCount: number;
  totalCount: number;
}

interface LoanOverviewData {
  totalCount: number;
  activeCount: number;
  totalBalance: number;
  totalMonthlyPayment: number;
  totalMonthlyTax: number;
  totalMonthlyInsurance: number;
  totalOriginalAmount: number;
}

interface TopVendor {
  vendorName: string;
  totalAmount: number;
  expenseCount: number;
}

interface UpcomingPayment {
  id: string;
  name: string;
  lender: string | null;
  paymentType: string;
  dueDate: string;
  minimumPayment: number;
  paymentAmount: number;
  currentBalance: number;
  interestRate: number;
  monthlyInterest: number;
  autopayEnabled: boolean;
  source: string;
}

interface BusinessProfile {
  id: string;
  name: string;
}

function getDatePreset(preset: string): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString().split('T')[0];
  let from: string;
  switch (preset) {
    case 'month':
      from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      break;
    case 'quarter': {
      const qm = Math.floor(now.getMonth() / 3) * 3;
      from = new Date(now.getFullYear(), qm, 1).toISOString().split('T')[0];
      break;
    }
    case 'year':
      from = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
      break;
    default:
      from = '';
  }
  return { from, to };
}

// Prior comparable period (same length, immediately preceding) for delta calc.
function getPriorPeriod(preset: string): { from: string; to: string } {
  const now = new Date();
  switch (preset) {
    case 'month': {
      const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const to = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: from.toISOString().split('T')[0], to: to.toISOString().split('T')[0] };
    }
    case 'quarter': {
      const qm = Math.floor(now.getMonth() / 3) * 3;
      const from = new Date(now.getFullYear(), qm - 3, 1);
      const to = new Date(now.getFullYear(), qm, 0);
      return { from: from.toISOString().split('T')[0], to: to.toISOString().split('T')[0] };
    }
    case 'year': {
      const from = new Date(now.getFullYear() - 1, 0, 1);
      const to = new Date(now.getFullYear() - 1, 11, 31);
      return { from: from.toISOString().split('T')[0], to: to.toISOString().split('T')[0] };
    }
    default:
      return { from: '', to: '' };
  }
}

function getWeekStart(): string {
  const now = new Date();
  const day = now.getDay(); // 0=Sun
  const diff = now.getDate() - day; // start on Sunday
  const weekStart = new Date(now.getFullYear(), now.getMonth(), diff);
  return weekStart.toISOString().split('T')[0];
}

function getWeekEnd(): string {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + 6;
  const weekEnd = new Date(now.getFullYear(), now.getMonth(), diff);
  return weekEnd.toISOString().split('T')[0];
}

const paymentTypeLabel: Record<string, string> = {
  credit_card: 'Credit Card',
  store_card: 'Store Card',
  business_line: 'Business Line',
  mortgage: 'Mortgage',
  auto: 'Auto Loan',
  equipment: 'Equipment Loan',
  business: 'Business Loan',
  personal: 'Personal Loan',
  line_of_credit: 'Line of Credit',
  subscription: 'Subscription',
  expense: 'Recurring Expense',
  other: 'Other',
};

export function ExpenseOverview() {
  const { toast } = useToast();
  const [period, setPeriod] = useState('year');
  const [profileFilter, setProfileFilter] = useState('');

  const dateRange = period === 'all' ? { from: '', to: '' } : getDatePreset(period);
  const priorRange = period === 'all' ? { from: '', to: '' } : getPriorPeriod(period);

  const { data: profilesData } = useQuery({
    queryKey: ['businessProfiles'],
    queryFn: () => api.getBusinessProfiles(),
  });

  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['expenseSummary', { dateFrom: dateRange.from, dateTo: dateRange.to, businessProfileId: profileFilter }],
    queryFn: () => api.getExpenseSummary({
      dateFrom: dateRange.from || undefined,
      dateTo: dateRange.to || undefined,
      businessProfileId: profileFilter || undefined,
    }),
  });

  // Prior comparable period — only fetched when a preset is active (not 'all').
  const { data: priorSummaryData } = useQuery({
    queryKey: ['expenseSummaryPrior', { dateFrom: priorRange.from, dateTo: priorRange.to, businessProfileId: profileFilter }],
    queryFn: () => api.getExpenseSummary({
      dateFrom: priorRange.from || undefined,
      dateTo: priorRange.to || undefined,
      businessProfileId: profileFilter || undefined,
    }),
    enabled: period !== 'all' && !!priorRange.from,
  });

  // Expected monthly burn by category (forward-looking, cash-flow lens).
  // Independent of the date filter — recurring obligations don't have a date range.
  const { data: expectedMonthlyData } = useQuery({
    queryKey: ['expectedMonthlyByCategory', { businessProfileId: profileFilter }],
    queryFn: () => api.getExpectedMonthlyByCategory({
      businessProfileId: profileFilter || undefined,
    }),
  });

  // Subscription overview — kept for the "expiring within 30 days" alert.
  const { data: subOverviewData } = useQuery({
    queryKey: ['subscriptionOverview', { businessProfileId: profileFilter }],
    queryFn: () => api.getSubscriptionOverview({
      businessProfileId: profileFilter || undefined,
    }),
  });

  // All recurring obligations grouped by native cadence — unions
  // subscriptions + recurring expenses (excludes loans, which have their own panel).
  const { data: recurringByCycleData } = useQuery({
    queryKey: ['recurringByCycle', { businessProfileId: profileFilter }],
    queryFn: () => api.getRecurringByCycle({
      businessProfileId: profileFilter || undefined,
    }),
  });

  // Loan overview — for cash flow (principal is not an expense but is cash outflow)
  const { data: loanOverviewData } = useQuery({
    queryKey: ['loanOverview', { businessProfileId: profileFilter }],
    queryFn: () => api.getLoanOverview({
      businessProfileId: profileFilter || undefined,
    }),
  });

  // Top vendors by spend in current period.
  const { data: topVendorsData } = useQuery({
    queryKey: ['topVendors', { dateFrom: dateRange.from, dateTo: dateRange.to, businessProfileId: profileFilter }],
    queryFn: () => api.getTopVendors({
      dateFrom: dateRange.from || undefined,
      dateTo: dateRange.to || undefined,
      businessProfileId: profileFilter || undefined,
      limit: 5,
    }),
  });

  // Upcoming payments for this week
  const weekStart = getWeekStart();
  const { data: upcomingData } = useQuery({
    queryKey: ['upcomingPayments', { weekStart, businessProfileId: profileFilter }],
    queryFn: () => api.getUpcomingPayments({
      weekStart,
      businessProfileId: profileFilter || undefined,
    }),
  });

  const profiles = (profilesData?.data || []) as BusinessProfile[];
  const summary = (summaryData?.data as ExpenseSummaryData)?.summary;
  const categories = (summaryData?.data as ExpenseSummaryData)?.categories || [];
  const subOverview = subOverviewData?.data as SubscriptionOverviewData | undefined;
  const recurringByCycle = recurringByCycleData?.data as RecurringByCycleData | undefined;
  const loanOverview = loanOverviewData?.data as LoanOverviewData | undefined;
  const upcomingPayments = (upcomingData?.data || []) as UpcomingPayment[];
  const topVendors = (topVendorsData?.data || []) as TopVendor[];
  const expectedMonthly = (expectedMonthlyData?.data as ExpectedMonthlyData | undefined);
  const expectedCategories = expectedMonthly?.categories || [];
  const expectedTotalMonthly = expectedMonthly?.totalMonthly || 0;
  const maxExpectedAmount = expectedCategories.length > 0
    ? Math.max(...expectedCategories.map(c => c.monthlyAmount))
    : 1;

  const syncToOutlookMutation = useMutation({
    mutationFn: () => api.syncPaymentTasksToOutlook({
      weekStart,
      businessProfileId: profileFilter || undefined,
    }),
    onSuccess: (result) => {
      const data = result?.data as { synced: number; total: number } | undefined;
      if (data && data.synced > 0) {
        toast({ title: `Synced ${data.synced} payment${data.synced !== 1 ? 's' : ''} to Outlook Tasks` });
      } else if (data && data.synced === 0) {
        toast({ title: 'All payments already synced to Outlook' });
      }
    },
    onError: () => {
      toast({ title: 'Failed to sync to Outlook', description: 'Make sure your Outlook account is connected.', variant: 'destructive' });
    },
  });

  const isLoading = summaryLoading;

  // totalSpend already includes subscriptions (joined at database level)
  const totalBusinessExpenses = summary?.totalSpend || 0;
  const subscriptionCategory = categories.find(c => c.categorySlug === 'subscriptions');
  const subscriptionsTotal = subscriptionCategory?.totalAmount || 0;
  const commonExpensesTotal = totalBusinessExpenses - subscriptionsTotal;

  // Period-over-period delta on total spend.
  const priorSummary = (priorSummaryData?.data as ExpenseSummaryData | undefined)?.summary;
  const priorTotal = priorSummary?.totalSpend || 0;
  const periodDeltaPct = priorTotal > 0
    ? ((totalBusinessExpenses - priorTotal) / priorTotal) * 100
    : null;
  const periodDeltaLabel: Record<string, string> = {
    month: 'vs last month',
    quarter: 'vs last quarter',
    year: 'vs last year',
  };

  // Categories excluding subscriptions (shown separately in breakdown)
  const commonCategories = categories.filter(c => c.categorySlug !== 'subscriptions');

  // Forward-looking loan summary (no period mismatch — these are monthly amounts).
  const loanMonthlyTotal = loanOverview?.totalMonthlyPayment || 0;
  const loanEscrowTotal = (loanOverview?.totalMonthlyTax || 0) + (loanOverview?.totalMonthlyInsurance || 0);
  const loanPIPayment = loanMonthlyTotal - loanEscrowTotal;
  const loanPaidDownPct = loanOverview && loanOverview.totalOriginalAmount > 0
    ? (1 - loanOverview.totalBalance / loanOverview.totalOriginalAmount) * 100
    : 0;

  return (
    <div className="space-y-6">
      {/* Stat Cards — top row */}
      {!isLoading && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <Card className="border-l-4 border-l-primary bg-primary/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                  <DollarSign className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted-foreground">Total Business Expenses</p>
                  <p className="text-2xl font-bold tabular-nums truncate">{formatCurrency(totalBusinessExpenses)}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {formatCurrency(commonExpensesTotal)} common + {formatCurrency(subscriptionsTotal)} subs
                  </p>
                  {periodDeltaPct !== null && (
                    <div className={`flex items-center gap-1 text-xs mt-0.5 ${
                      periodDeltaPct > 0 ? 'text-red-600' : periodDeltaPct < 0 ? 'text-green-600' : 'text-muted-foreground'
                    }`}>
                      {periodDeltaPct > 0
                        ? <TrendingUp className="h-3 w-3" />
                        : periodDeltaPct < 0
                          ? <TrendingDown className="h-3 w-3" />
                          : <Minus className="h-3 w-3" />}
                      <span className="tabular-nums">{periodDeltaPct > 0 ? '+' : ''}{periodDeltaPct.toFixed(1)}%</span>
                      <span className="text-muted-foreground">{periodDeltaLabel[period]}</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-violet-500/10 shrink-0">
                  <Receipt className="h-5 w-5 text-violet-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Common Expenses</p>
                  <p className="text-2xl font-bold tabular-nums truncate">{formatCurrency(commonExpensesTotal)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10 shrink-0">
                  <Repeat className="h-5 w-5 text-blue-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Subscriptions</p>
                  <p className="text-2xl font-bold tabular-nums truncate">{formatCurrency(subscriptionsTotal)}</p>
                  <p className="text-xs text-muted-foreground">{subscriptionCategory?.expenseCount || 0} active</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10 shrink-0">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Tax Deductible</p>
                  <p className="text-2xl font-bold tabular-nums truncate">{formatCurrency(summary?.taxDeductibleTotal || 0)}</p>
                  <p className="text-xs text-muted-foreground">
                    {totalBusinessExpenses > 0
                      ? `${((summary?.taxDeductibleTotal || 0) / totalBusinessExpenses * 100).toFixed(0)}% of total`
                      : '—'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/10 shrink-0">
                  <Hash className="h-5 w-5 text-orange-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Total Records</p>
                  <p className="text-2xl font-bold tabular-nums truncate">{summary?.expenseCount || 0}</p>
                  <p className="text-xs text-muted-foreground">{summary?.categoryCount || 0} categories</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Time period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="quarter">This Quarter</SelectItem>
            <SelectItem value="year">This Year</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>
        {profiles.length > 1 && (
          <Select value={profileFilter} onValueChange={(v) => setProfileFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All profiles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All profiles</SelectItem>
              {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Payments Due This Week */}
      {upcomingPayments.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <CalendarClock className="h-4 w-4" />
              Payments Due This Week
            </h3>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">
                {new Date(weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} &ndash; {new Date(getWeekEnd()).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => syncToOutlookMutation.mutate()}
                disabled={syncToOutlookMutation.isPending}
              >
                {syncToOutlookMutation.isPending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                Sync to Outlook
              </Button>
            </div>
          </div>
          <Card className="border-amber-500/20">
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-amber-500/5">
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Due Date</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Account</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Type</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Payment</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Interest</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingPayments.map((pmt) => {
                    const dueDate = new Date(pmt.dueDate);
                    const isOverdue = dueDate < new Date(new Date().toISOString().split('T')[0]);
                    const isToday = pmt.dueDate === new Date().toISOString().split('T')[0];
                    return (
                      <tr key={`${pmt.source}-${pmt.id}`} className={`border-b ${isOverdue ? 'bg-red-500/5' : isToday ? 'bg-amber-500/5' : 'hover:bg-muted/30'}`}>
                        <td className="p-3 text-sm">
                          <span className={isOverdue ? 'text-red-600 font-medium' : isToday ? 'text-amber-600 font-medium' : ''}>
                            {dueDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                          </span>
                          {isOverdue && <span className="ml-1 text-xs text-red-500">overdue</span>}
                          {isToday && <span className="ml-1 text-xs text-amber-500">today</span>}
                        </td>
                        <td className="p-3 text-sm">
                          <div className="flex items-center gap-2">
                            {pmt.source === 'charge_account' ? <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
                              : pmt.source === 'subscription' ? <RepeatIcon className="h-3.5 w-3.5 text-muted-foreground" />
                              : <Landmark className="h-3.5 w-3.5 text-muted-foreground" />}
                            <div>
                              <span className="font-medium">{pmt.name}</span>
                              {pmt.lender && <span className="text-xs text-muted-foreground ml-1">({pmt.lender})</span>}
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">{paymentTypeLabel[pmt.paymentType] || pmt.paymentType}</td>
                        <td className="p-3 text-sm text-right font-semibold">{formatCurrency(pmt.paymentAmount)}</td>
                        <td className="p-3 text-sm text-right text-amber-600">
                          {pmt.monthlyInterest > 0 ? formatCurrency(pmt.monthlyInterest) : '—'}
                        </td>
                        <td className="p-3 text-sm text-right text-muted-foreground">{formatCurrency(pmt.currentBalance)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/30">
                    <td colSpan={3} className="p-3 text-sm font-semibold">Week Total</td>
                    <td className="p-3 text-sm text-right font-bold">
                      {formatCurrency(upcomingPayments.reduce((sum, p) => sum + p.paymentAmount, 0))}
                    </td>
                    <td className="p-3 text-sm text-right text-amber-600 font-medium">
                      {formatCurrency(upcomingPayments.reduce((sum, p) => sum + p.monthlyInterest, 0))}
                    </td>
                    <td className="p-3"></td>
                  </tr>
                </tfoot>
              </table>
            </CardContent>
          </Card>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <>
          {/* Breakdown + Category Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Breakdown + Subscription Detail */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Expense Breakdown</h3>

              <Card>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Receipt className="h-4 w-4 text-violet-500" />
                      <span className="text-sm font-medium">Common Expenses</span>
                    </div>
                    <span className="text-sm font-bold">{formatCurrency(commonExpensesTotal)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6">
                    {commonCategories.length} categories &middot; {(summary?.expenseCount || 0) - (subscriptionCategory?.expenseCount || 0)} records
                  </p>

                  <div className="border-t pt-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Repeat className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium">Subscriptions</span>
                    </div>
                    <span className="text-sm font-bold">{formatCurrency(subscriptionsTotal)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6">
                    {subscriptionCategory?.expenseCount || 0} active subscriptions
                  </p>

                  <div className="border-t pt-3 flex items-center justify-between font-semibold">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-primary" />
                      <span className="text-sm">Total Business Expenses</span>
                    </div>
                    <span className="text-sm">{formatCurrency(totalBusinessExpenses)}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Loans & Mortgages — forward-looking loan summary */}
              {loanOverview && loanOverview.activeCount > 0 && (
                <>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide pt-2">Loans &amp; Mortgages</h3>
                  <Card className="border-amber-500/20 bg-amber-500/5">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex justify-between items-baseline text-sm">
                        <span className="text-muted-foreground flex items-center gap-2">
                          <Landmark className="h-4 w-4 text-amber-600" />
                          Active loans
                        </span>
                        <span className="font-medium tabular-nums">{loanOverview.activeCount}</span>
                      </div>
                      <div className="flex justify-between items-baseline text-sm">
                        <span className="text-muted-foreground">Outstanding balance</span>
                        <span className="font-medium tabular-nums">{formatCurrency(loanOverview.totalBalance)}</span>
                      </div>
                      <div className="flex justify-between items-baseline text-sm">
                        <span className="text-muted-foreground">Monthly P&amp;I</span>
                        <span className="font-medium tabular-nums">{formatCurrency(loanPIPayment)}<span className="text-muted-foreground font-normal">/mo</span></span>
                      </div>
                      {loanEscrowTotal > 0 && (
                        <div className="flex justify-between items-baseline text-sm">
                          <span className="text-muted-foreground">Escrow (tax + insurance)</span>
                          <span className="font-medium tabular-nums">{formatCurrency(loanEscrowTotal)}<span className="text-muted-foreground font-normal">/mo</span></span>
                        </div>
                      )}
                      <div className="pt-2 mt-1 border-t flex justify-between items-baseline text-sm">
                        <span className="font-semibold">Total monthly payment</span>
                        <span className="font-bold tabular-nums">{formatCurrency(loanMonthlyTotal)}<span className="text-muted-foreground font-normal">/mo</span></span>
                      </div>
                      {loanOverview.totalOriginalAmount > 0 && (
                        <div className="pt-1 flex justify-between items-baseline text-xs">
                          <span className="text-muted-foreground">Paid down</span>
                          <span className="text-muted-foreground tabular-nums">
                            {loanPaidDownPct.toFixed(1)}% of {formatCurrency(loanOverview.totalOriginalAmount)} original
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Top 5 Vendors — concentration view for the selected period */}
              {topVendors.length > 0 && (
                <>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide pt-2">
                    Top Vendors
                  </h3>
                  <Card>
                    <CardContent className="p-4 space-y-2">
                      {topVendors.map((v) => (
                        <div key={v.vendorName} className="flex items-baseline justify-between text-sm gap-2">
                          <span className="truncate">
                            {v.vendorName}
                            <span className="text-xs text-muted-foreground ml-1">({v.expenseCount})</span>
                          </span>
                          <span className="font-medium tabular-nums shrink-0">{formatCurrency(v.totalAmount)}</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Recurring Obligations by Cadence — each native rate plus its monthly equivalent.
                  Unions subscriptions + recurring expenses. Loans are tracked separately above. */}
              {recurringByCycle && recurringByCycle.totalCount > 0 && (() => {
                const monthlyEquivWeekly = recurringByCycle.totalWeeklyCost * 52 / 12;
                const monthlyEquivMonthly = recurringByCycle.totalMonthlyCost;
                const monthlyEquivQuarterly = recurringByCycle.totalQuarterlyCost / 3;
                const monthlyEquivSemiAnnual = recurringByCycle.totalSemiAnnualCost / 6;
                const monthlyEquivAnnual = recurringByCycle.totalAnnualCost / 12;
                const totalMonthlyEquivalent =
                  monthlyEquivWeekly + monthlyEquivMonthly + monthlyEquivQuarterly + monthlyEquivSemiAnnual + monthlyEquivAnnual;
                return (
                  <>
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide pt-2">
                      Recurring Obligations by Cadence
                    </h3>
                    <Card>
                      <CardContent className="p-4 space-y-2">
                        {recurringByCycle.totalWeeklyCost > 0 && (
                          <div className="flex justify-between items-baseline text-sm">
                            <span className="text-muted-foreground">Weekly <span className="text-xs">({recurringByCycle.weeklyCount})</span></span>
                            <span>
                              <span className="font-medium tabular-nums">{formatCurrency(recurringByCycle.totalWeeklyCost)}</span>
                              <span className="text-muted-foreground">/wk</span>
                              <span className="text-xs text-muted-foreground ml-2">≈ {formatCurrency(monthlyEquivWeekly)}/mo</span>
                            </span>
                          </div>
                        )}
                        {recurringByCycle.totalMonthlyCost > 0 && (
                          <div className="flex justify-between items-baseline text-sm">
                            <span className="text-muted-foreground">Monthly <span className="text-xs">({recurringByCycle.monthlyCount})</span></span>
                            <span>
                              <span className="font-medium tabular-nums">{formatCurrency(recurringByCycle.totalMonthlyCost)}</span>
                              <span className="text-muted-foreground">/mo</span>
                            </span>
                          </div>
                        )}
                        {recurringByCycle.totalQuarterlyCost > 0 && (
                          <div className="flex justify-between items-baseline text-sm">
                            <span className="text-muted-foreground">Quarterly <span className="text-xs">({recurringByCycle.quarterlyCount})</span></span>
                            <span>
                              <span className="font-medium tabular-nums">{formatCurrency(recurringByCycle.totalQuarterlyCost)}</span>
                              <span className="text-muted-foreground">/qtr</span>
                              <span className="text-xs text-muted-foreground ml-2">≈ {formatCurrency(monthlyEquivQuarterly)}/mo</span>
                            </span>
                          </div>
                        )}
                        {recurringByCycle.totalSemiAnnualCost > 0 && (
                          <div className="flex justify-between items-baseline text-sm">
                            <span className="text-muted-foreground">Semi-Annual <span className="text-xs">({recurringByCycle.semiAnnualCount})</span></span>
                            <span>
                              <span className="font-medium tabular-nums">{formatCurrency(recurringByCycle.totalSemiAnnualCost)}</span>
                              <span className="text-muted-foreground">/6mo</span>
                              <span className="text-xs text-muted-foreground ml-2">≈ {formatCurrency(monthlyEquivSemiAnnual)}/mo</span>
                            </span>
                          </div>
                        )}
                        {recurringByCycle.totalAnnualCost > 0 && (
                          <div className="flex justify-between items-baseline text-sm">
                            <span className="text-muted-foreground">Annual <span className="text-xs">({recurringByCycle.annualCount})</span></span>
                            <span>
                              <span className="font-medium tabular-nums">{formatCurrency(recurringByCycle.totalAnnualCost)}</span>
                              <span className="text-muted-foreground">/yr</span>
                              <span className="text-xs text-muted-foreground ml-2">≈ {formatCurrency(monthlyEquivAnnual)}/mo</span>
                            </span>
                          </div>
                        )}
                        <div className="pt-2 mt-1 border-t flex justify-between items-baseline text-sm">
                          <span className="font-semibold">Total monthly equivalent</span>
                          <span className="font-bold tabular-nums">
                            {formatCurrency(totalMonthlyEquivalent)}<span className="text-muted-foreground font-normal">/mo</span>
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground pt-1">
                          Includes subscriptions + recurring expenses. Loans shown separately.
                        </p>
                        {subOverview && subOverview.expiringCount > 0 && (
                          <div className="flex items-center gap-2 pt-2 border-t text-sm text-amber-600">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {subOverview.expiringCount} subscription{subOverview.expiringCount !== 1 ? 's' : ''} expiring within 30 days
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </>
                );
              })()}
            </div>

            {/* Right: Expected Monthly Costs by Category — forward-looking burn rate */}
            <div className="lg:col-span-2 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Expected Monthly Costs by Category
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Recurring obligations normalized to a monthly cadence. Note: What hits your account every month.
                  Excludes one-off purchases.
                </p>
              </div>
              <Card>
                <CardContent className="p-4">
                  {expectedCategories.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No recurring obligations yet. Mark expenses as recurring (or add subscriptions/loans) to see your monthly burn.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {expectedCategories.map((cat) => {
                        const pct = expectedTotalMonthly > 0 ? (cat.monthlyAmount / expectedTotalMonthly) * 100 : 0;
                        const barWidth = maxExpectedAmount > 0 ? (cat.monthlyAmount / maxExpectedAmount) * 100 : 0;
                        return (
                          <div key={cat.categorySlug} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="font-medium">{cat.categoryName}</span>
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-muted-foreground">{cat.itemCount} item{cat.itemCount !== 1 ? 's' : ''}</span>
                                <span className="font-semibold w-28 text-right tabular-nums">
                                  {formatCurrency(cat.monthlyAmount)}<span className="text-muted-foreground text-xs">/mo</span>
                                </span>
                                <span className="text-xs text-muted-foreground w-12 text-right tabular-nums">{pct.toFixed(1)}%</span>
                              </div>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{
                                  width: `${barWidth}%`,
                                  backgroundColor: cat.categorySlug === 'subscriptions' ? 'rgb(59 130 246 / 0.7)' : 'hsl(var(--primary) / 0.7)',
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                      {/* Total monthly burn footer */}
                      <div className="pt-3 mt-2 border-t flex items-center justify-between">
                        <span className="text-sm font-semibold">Total expected monthly</span>
                        <span className="text-lg font-bold tabular-nums">
                          {formatCurrency(expectedTotalMonthly)}<span className="text-sm text-muted-foreground font-normal">/mo</span>
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
